import React, { useMemo } from 'react';

// Helper to format numbers in French locale
const formatLocaleNumber = (value, decimals = 2) => Number(value || 0).toFixed(decimals).replace('.', ',');

const getUnitPriceFromMovement = (movement) => {
    if (!movement) return null;
    const batch = movement.batch || {};
    const batchTotalPrice = Number(batch?.unit_cost ?? null);
    const batchReceivedQty = Number(batch?.received_quantity ?? null);
    if (batchTotalPrice > 0 && batchReceivedQty > 0) {
        return batchTotalPrice / batchReceivedQty;
    }
    const mvTotalPrice = Number(movement?.unit_cost ?? null);
    const mvQty = Number(movement?.quantity ?? null);
    if (mvTotalPrice > 0 && mvQty > 0) return mvTotalPrice / mvQty;
    return null;
};

export default function SuppliersComparison({ items = [], movements = [], supplyOrders = [], suppliers = [] }) {
    const supplierMap = useMemo(
        () => new Map(suppliers.map((item) => [item.id, item])),
        [suppliers]
    );

    const getMovementSupplierName = (movement) => {
        const batch = movement?.batch || {};
        const supplierName = movement?.supplier_name || batch?.supplier_name || null;
        if (typeof supplierName === 'string' && supplierName.trim()) {
            return supplierName.trim();
        }

        const batchSupplier = batch?.supplier;
        if (batchSupplier) {
            if (typeof batchSupplier === 'string') return batchSupplier;
            return batchSupplier.companyName || batchSupplier.company_name || batchSupplier.name || batchSupplier.supplier_name || batchSupplier.label || '—';
        }

        const directSupplier = movement?.supplier;
        if (directSupplier) {
            if (typeof directSupplier === 'string') return directSupplier;
            return directSupplier.companyName || directSupplier.company_name || directSupplier.name || directSupplier.supplier_name || directSupplier.label || '—';
        }

        return '—';
    };

    const getOrderSupplierName = (order) => {
        if (!order) return '—';
        const supplierName = order.supplier || order.supplier_name || order.companyName || order.company_name || order.provider || null;
        if (typeof supplierName === 'string' && supplierName.trim()) {
            return supplierName.trim();
        }

        const orderSupplier = order?.supplier;
        if (orderSupplier && typeof orderSupplier === 'object') {
            return orderSupplier.companyName || orderSupplier.company_name || orderSupplier.name || orderSupplier.supplier_name || orderSupplier.label || '—';
        }

        const supplierId = Number(order.supplierId ?? order.supplier_id ?? order.supplierID ?? 0) || null;
        if (supplierId) {
            const found = supplierMap.get(supplierId);
            if (found) {
                return found.companyName || found.company_name || found.name || '—';
            }
        }

        return '—';
    };

    const grouped = useMemo(() => {
        const purchases = (movements || []).filter((m) => {
            const type = String(m.type || '').toLowerCase();
            return type === 'purchase';
        });

        const byItem = {};
        const addSupplierEntry = (itemId, supplier, price, date) => {
            if (!itemId) return;
            const key = String(itemId);
            if (!byItem[key]) byItem[key] = {};
            if (!byItem[key][supplier]) byItem[key][supplier] = { supplier, prices: [] };
            byItem[key][supplier].prices.push({ price, date });
        };

        purchases.forEach((m) => {
            const movementTargetId = m?.stockable_id ?? m?.stockable?.id ?? m?.ingredient_id ?? m?.ingredient?.id ?? m?.item_id ?? m?.item?.id;
            const itemId = String(movementTargetId || '');
            if (!itemId) return;
            const supplier = getMovementSupplierName(m);
            const unitPrice = getUnitPriceFromMovement(m);
            addSupplierEntry(itemId, supplier, unitPrice, m.batch?.purchase_date || m.movement_date || m.created_at);
        });

        (supplyOrders || []).forEach((order) => {
            const itemId = String(order.target_id ?? order.targetId ?? '');
            if (!itemId) return;
            const supplier = getOrderSupplierName(order);
            const price = Number(order.purchase_price ?? order.purchasePrice ?? 0);
            const date = order.purchase_date || order.purchaseDate || order.order_date || order.orderDate || order.created_at || null;
            addSupplierEntry(itemId, supplier, price > 0 ? price : null, date);
        });

        // Convert to array and sort by latest price
        const result = {};
        Object.entries(byItem).forEach(([itemId, suppliers]) => {
            result[itemId] = Object.values(suppliers).map((s) => {
                const latest = s.prices
                    .filter((p) => p.price !== null && !Number.isNaN(Number(p.price)))
                    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))[0];
                return {
                    supplier: s.supplier,
                    latestPrice: latest ? Number(latest.price) : null,
                    lastDate: latest ? latest.date : null,
                    allPrices: s.prices,
                };
            }).sort((a, b) => (a.latestPrice || Infinity) - (b.latestPrice || Infinity));
        });

        return result;
    }, [movements, supplyOrders]);

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl">
                <h2 className="text-2xl font-semibold text-green-900">Comparaison fournisseurs</h2>
                <p className="text-sm text-green-600">Liste des articles et comparaison des fournisseurs basée sur les achats (BC).</p>
            </section>

            <section className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                        <thead className="bg-green-100 text-green-900">
                            <tr>
                                <th className="px-4 py-3">Article</th>
                                <th className="px-4 py-3">Fournisseurs / Dernier prix</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-green-100 bg-white">
                            {(items || []).map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50 even:bg-white">
                                    <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-2">
                                            {((grouped[String(item.id)] || [])).length === 0 ? (
                                                <div className="text-sm text-green-600">Aucun achat enregistré (BC) pour cet article.</div>
                                            ) : (
                                                (grouped[String(item.id)] || []).map((s) => (
                                                    <div key={s.supplier} className="flex items-center justify-between rounded-2xl bg-green-50 px-3 py-2">
                                                        <div className="text-sm text-slate-900">{s.supplier}</div>
                                                        <div className="text-sm font-semibold text-green-900">{s.latestPrice ? `${formatLocaleNumber(s.latestPrice, 2)} Ar` : '—'}</div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
